import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  BadgeCheck,
  CircleAlert,
  Fingerprint,
  Receipt,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  ThumbsUp,
} from 'lucide-react';
import { api } from '@/lib/api';
import { formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/cn';
import { Badge, Button, Spinner } from '@/components/ui';
import type { VehicleInsight, VehicleNote } from '@/lib/types';

const SEVERITY = {
  0: { label: 'Prednost', tone: 'text-success', ring: 'border-success/30 bg-success/8', icon: ThumbsUp },
  1: { label: 'Sitnica', tone: 'text-muted', ring: 'border-default bg-sunken', icon: CircleAlert },
  2: { label: 'Pazi', tone: 'text-gold', ring: 'border-gold/30 bg-gold/8', icon: AlertTriangle },
  3: { label: 'Ozbiljno', tone: 'text-danger', ring: 'border-danger/30 bg-danger/8', icon: ShieldAlert },
} as const;

function NoteCard({ note, index }: { note: VehicleNote; index: number }) {
  const severity = SEVERITY[note.severity as keyof typeof SEVERITY] ?? SEVERITY[1];
  const Icon = severity.icon;

  return (
    <li
      style={{ '--i': Math.min(index, 9) } as React.CSSProperties}
      className={cn(
        'animate-fade-in-up stagger rounded-xl border p-3.5 transition duration-300',
        severity.ring,
      )}
    >
      <div className="flex items-start gap-2.5">
        <Icon size={16} className={cn('mt-0.5 shrink-0', severity.tone)} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h4 className="text-sm font-semibold text-body">{note.title}</h4>
            <span className={cn('text-[10px] font-bold tracking-wide uppercase', severity.tone)}>
              {severity.label}
            </span>
          </div>

          <p className="mt-1 text-[13px] leading-relaxed text-muted">{note.description}</p>

          {note.typicalMileage && (
            <p
              className={cn(
                'mt-2 text-[11px] tabular-nums',
                note.pastTypicalMileage ? 'font-medium text-gold' : 'text-faint',
              )}
            >
              {note.pastTypicalMileage
                ? `Ovaj auto je već prešao ~${formatNumber(note.typicalMileage)} km — proveri obavezno.`
                : `Obično se javlja oko ${formatNumber(note.typicalMileage)} km.`}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * What the site knows about this car that the seller did not type.
 *
 * Four things, in the order a buyer cares about them: does the VIN agree with the advertisement,
 * what does this generation break, what will it cost to keep on the road, and has anyone checked
 * its history. The first three need no provider and no key, which is why they render even when the
 * fourth is unavailable.
 */
export function VehicleInsightPanel({
  listingId,
  isOwnedByCaller,
}: {
  listingId: string;
  isOwnedByCaller: boolean;
}) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['insight', listingId],
    queryFn: () => api<VehicleInsight>(`/api/listings/${listingId}/insight`),
  });

  const check = useMutation({
    mutationFn: () => api(`/api/listings/${listingId}/history-check`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insight', listingId] }),
  });

  if (isPending) {
    return (
      <div className="mt-10 flex justify-center py-8">
        <Spinner className="size-6 text-accent" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const strengths = data.notes.filter((note) => note.severity === 0);
  const faults = data.notes.filter((note) => note.severity > 0);
  const shownFaults = expanded ? faults : faults.slice(0, 3);

  return (
    <section className="mt-12 space-y-5">
      <div className="flex items-center gap-2">
        <Sparkles size={18} className="text-gold" />
        <h2 className="text-lg font-semibold text-body">Šta još treba da znate</h2>
      </div>

      {/* --- VIN ------------------------------------------------------------ */}
      {data.vin && (
        <div className="animate-fade-in-up rounded-[var(--radius-panel)] border border-default bg-raised p-5 shadow-card">
          <div className="flex items-center gap-2">
            <Fingerprint size={16} className="text-accent" />
            <h3 className="text-sm font-semibold text-body">Provera broja šasije</h3>

            {data.vin.warnings.length === 0 ? (
              <Badge tone="success" className="ml-auto">
                <BadgeCheck size={12} />
                Bez neslaganja
              </Badge>
            ) : (
              <Badge tone="danger" className="ml-auto">
                <AlertTriangle size={12} />
                {data.vin.warnings.length}
              </Badge>
            )}
          </div>

          <dl className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
            <div>
              <dt className="text-faint">Format</dt>
              <dd className={cn('font-medium', data.vin.isWellFormed ? 'text-success' : 'text-danger')}>
                {data.vin.isWellFormed ? 'Ispravan' : 'Neispravan'}
              </dd>
            </div>
            <div>
              <dt className="text-faint">Godište iz VIN-a</dt>
              <dd className="font-medium text-body tabular-nums">{data.vin.modelYear ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-faint">Zemlja proizvodnje</dt>
              <dd className="font-medium text-body">{data.vin.country ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-faint">Proizvođač iz VIN-a</dt>
              <dd className="font-medium text-body">{data.vin.manufacturerHint ?? '—'}</dd>
            </div>
          </dl>

          {data.vin.warnings.length > 0 && (
            <ul className="mt-3 space-y-1.5 border-t border-default pt-3">
              {data.vin.warnings.map((warning) => (
                <li key={warning} className="flex items-start gap-2 text-[13px] text-danger">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  {warning}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* --- Known faults and strengths -------------------------------------- */}
      {data.notes.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-body">
            Poznato za ovu generaciju
            <span className="ml-2 text-xs font-normal text-faint">
              iskustva vlasnika i majstora, ne podaci iz oglasa
            </span>
          </h3>

          {strengths.length > 0 && (
            <ul className="space-y-2">
              {strengths.map((note, index) => (
                <NoteCard key={note.id} note={note} index={index} />
              ))}
            </ul>
          )}

          {faults.length > 0 && (
            <ul className="space-y-2">
              {shownFaults.map((note, index) => (
                <NoteCard key={note.id} note={note} index={index} />
              ))}
            </ul>
          )}

          {faults.length > 3 && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="press w-full rounded-xl border border-dashed border-default py-2 text-xs font-medium text-muted transition hover:border-accent hover:text-accent"
            >
              {expanded ? 'Prikaži manje' : `Prikaži još ${faults.length - 3}`}
            </button>
          )}
        </div>
      )}

      {/* --- Registration ---------------------------------------------------- */}
      <div className="animate-fade-in-up rounded-[var(--radius-panel)] border border-default bg-raised p-5 shadow-card">
        <div className="flex flex-wrap items-center gap-2">
          <Receipt size={16} className="text-accent" />
          <h3 className="text-sm font-semibold text-body">Procena godišnje registracije</h3>

          <span className="ml-auto text-right">
            <span className="block font-display text-xl font-bold text-body tabular-nums">
              ≈ {formatNumber(data.registration.totalEur)} €
            </span>
            <span className="block text-[11px] text-faint tabular-nums">
              {formatNumber(data.registration.totalRsd)} RSD
            </span>
          </span>
        </div>

        <dl className="mt-3 divide-y divide-default">
          {data.registration.lines.map((line) => (
            <div key={line.label} className="flex items-baseline justify-between gap-4 py-2">
              <dt className="min-w-0">
                <span className="block text-[13px] text-body">{line.label}</span>
                {line.note && <span className="block text-[11px] text-faint">{line.note}</span>}
              </dt>
              <dd className="shrink-0 text-[13px] font-medium text-body tabular-nums">
                {formatNumber(line.amountRsd)} RSD
              </dd>
            </div>
          ))}
        </dl>

        <ul className="mt-3 space-y-1 border-t border-default pt-3">
          {data.registration.assumptions.map((assumption) => (
            <li key={assumption} className="text-[11px] text-faint">
              {assumption}
            </li>
          ))}
          <li className="text-[11px] text-faint">
            Tarife za {data.registration.tariffYear}. godinu.
          </li>
        </ul>
      </div>

      {/* --- History check ---------------------------------------------------- */}
      {(data.history || data.historyAvailable) && (
        <div className="animate-fade-in-up rounded-[var(--radius-panel)] border border-default bg-raised p-5 shadow-card">
          <div className="flex flex-wrap items-center gap-2">
            {data.history?.status === 1 ? (
              <ShieldCheck size={16} className="text-success" />
            ) : (
              <ShieldAlert size={16} className="text-gold" />
            )}
            <h3 className="text-sm font-semibold text-body">Istorija vozila</h3>

            {data.history && (
              <Badge
                tone={
                  data.history.status === 1
                    ? 'success'
                    : data.history.status === 4
                      ? 'danger'
                      : 'neutral'
                }
                className="ml-auto"
              >
                {data.history.status === 1 && 'Provereno — čisto'}
                {data.history.status === 2 && 'Ima zabeleženih šteta'}
                {data.history.status === 3 && 'Nema podataka'}
                {data.history.status === 4 && 'Neslaganje!'}
                {data.history.status === 0 && 'U toku'}
              </Badge>
            )}
          </div>

          {data.history ? (
            <>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                <div>
                  <dt className="text-faint">Zabeležene štete</dt>
                  <dd className="font-medium text-body tabular-nums">
                    {data.history.damageRecords ?? '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-faint">Najviša kilometraža</dt>
                  <dd className="font-medium text-body tabular-nums">
                    {data.history.highestRecordedMileage
                      ? `${formatNumber(data.history.highestRecordedMileage)} km`
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-faint">Vlasnika</dt>
                  <dd className="font-medium text-body tabular-nums">
                    {data.history.recordedOwners ?? '—'}
                  </dd>
                </div>
                <div>
                  <dt className="text-faint">Provereno</dt>
                  <dd className="font-medium text-body">{formatDate(data.history.checkedAt)}</dd>
                </div>
              </dl>

              {data.history.mileageRollback && (
                <p className="mt-3 flex items-start gap-2 rounded-xl border border-danger/25 bg-danger/10 p-3 text-[13px] text-danger">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  U evidenciji postoji viša kilometraža nego što oglas navodi.
                </p>
              )}

              {data.history.isStale && (
                <p className="mt-3 text-[11px] text-faint">
                  Provera je zastarela ili je broj šasije izmenjen nakon nje.
                </p>
              )}

              {data.history.reportUrl && (
                <a
                  href={data.history.reportUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="mt-3 inline-block text-xs font-medium text-accent hover:underline"
                >
                  Pogledaj ceo izveštaj ({data.history.provider})
                </a>
              )}
            </>
          ) : (
            <p className="mt-2 text-[13px] text-muted">
              Ovaj oglas još nije proveren kroz servis istorije vozila.
            </p>
          )}

          {isOwnedByCaller && data.historyAvailable && (
            <Button
              size="sm"
              variant="secondary"
              className="mt-3"
              onClick={() => check.mutate()}
              loading={check.isPending}
            >
              {data.history ? 'Proveri ponovo' : 'Pokreni proveru'}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}
